/**
 * Página "Onde o consórcio ganha" do estudo: os pontos de empate com o
 * financiamento (empate.ts) e o gráfico do custo por prazo. Aparece sempre —
 * quando o plano escolhido perde, ela mostra a partir de onde ganharia, em vez
 * de deixar só o número negativo da página anterior.
 */
import { formatBRL } from '@/lib/format';
import type { Estudo } from './estudo';
import { COR, LINHA_CARTOES, caixaGrafico, cartaoNumero, larguraGrafico, pct, tituloSecao, type ModoPaginas } from './estudoMoldura';
import { graficoLinhas, type Marcador } from './graficosSvg';

const meses = (n: number) => `${n} ${n === 1 ? 'mês' : 'meses'}`;

/** No celular, cada plano numa linha: o nome e quanto economiza. */
const listaAlternativas = (e: Estudo) => {
  const alts = (e.opcoes.alternativas ?? []).slice(0, 6);
  if (alts.length === 0) return '';
  const linhas = alts
    .map((a) => {
      const cor = a.economia >= 0 ? COR.verde : '#9A6B00';
      return `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;padding:8px 0;border-top:1px solid #e6eaf0;">
        <span style="font-size:12px;font-weight:700;color:${COR.marinho};">Grupo ${a.grupo} · ${a.categoria}<br/><span style="font-size:10.5px;color:${COR.suave};font-weight:400;">${meses(a.prazo)} · taxa ${pct(a.taxaAdminPct)}${a.atual ? ' · este plano' : ''}</span></span>
        <span style="font-size:13px;font-weight:800;color:${cor};white-space:nowrap;">${a.economia >= 0 ? '' : '−'}${formatBRL(Math.abs(a.economia))}</span>
      </div>`;
    })
    .join('');
  return `<div style="margin-top:14px;border-radius:14px;background:${COR.fundo};padding:12px 14px;">
    <div style="font-size:12.5px;font-weight:700;color:${COR.marinho};margin-bottom:4px;">Planos com crédito parecido</div>
    ${linhas}
  </div>`;
};

/** Tabela dos planos com crédito parecido escolhidos pelo vendedor. */
const tabelaAlternativas = (e: Estudo) => {
  const alts = (e.opcoes.alternativas ?? []).slice(0, 6);
  if (alts.length === 0) return '';
  const th = (t: string, dir = false) =>
    `<th style="text-align:${dir ? 'right' : 'left'};padding:6px 8px;font-size:9.5px;letter-spacing:0.08em;text-transform:uppercase;color:${COR.suave};font-weight:700;">${t}</th>`;
  const linhas = alts
    .map((a) => {
      const cor = a.economia >= 0 ? COR.verde : '#9A6B00';
      const fundo = a.atual ? COR.ouroClaro : 'transparent';
      return `<tr style="background:${fundo};border-top:1px solid #e6eaf0;">
        <td style="padding:7px 8px;font-weight:700;color:${COR.marinho};">Grupo ${a.grupo} · ${a.categoria}${
          a.atual ? ` <span style="font-size:9px;color:${COR.ouro};letter-spacing:0.08em;text-transform:uppercase;">este plano</span>` : ''
        }</td>
        <td style="padding:7px 8px;text-align:right;">${formatBRL(a.credito)}</td>
        <td style="padding:7px 8px;text-align:right;">${meses(a.prazo)}</td>
        <td style="padding:7px 8px;text-align:right;">${pct(a.taxaAdminPct)}</td>
        <td style="padding:7px 8px;text-align:right;">${formatBRL(a.parcela)}</td>
        <td style="padding:7px 8px;text-align:right;font-weight:800;color:${cor};">${a.economia >= 0 ? '' : '−'}${formatBRL(Math.abs(a.economia))}</td>
      </tr>`;
    })
    .join('');
  return `<div style="margin-top:14px;border-radius:14px;background:${COR.fundo};padding:12px 14px;">
    <div style="font-size:12.5px;font-weight:700;color:${COR.marinho};margin-bottom:4px;">Planos com crédito parecido</div>
    <table style="width:100%;border-collapse:collapse;font-size:10.5px;color:${COR.texto};">
      <tr>${th('Plano')}${th('Crédito', true)}${th('Prazo', true)}${th('Taxa', true)}${th('Parcela', true)}${th(`Frente ao ${e.empate!.principal.sistema}`, true)}</tr>
      ${linhas}
    </table>
  </div>`;
};

export const paginaEmpate = (e: Estudo, num: string, modo: ModoPaginas = 'pdf') => {
  const curto = modo === 'celular';
  const { principal, outro } = e.empate!;
  const prazo = e.entrada.prazo;
  const taxa = e.entrada.taxaAdminPct;
  const juros = e.taxas.jurosFinanciamentoAa;
  const ganha = principal.consorcioGanha;
  const nomePrincipal = `Financiamento ${principal.sistema}`;
  const nomeOutro = `Financiamento ${outro.sistema}`;
  const corPrincipal = principal.sistema === 'SAC' ? COR.azulClaro : COR.vermelho;
  const corOutro = outro.sistema === 'SAC' ? COR.azulClaro : COR.vermelho;
  const horizonte = principal.curva.length;

  const marcadores: Marcador[] = [{ indice: Math.min(prazo, horizonte) - 1, rotulo: `Este plano · ${meses(prazo)}` }];
  if (principal.prazoMeses && principal.prazoMeses !== prazo && principal.prazoMeses <= horizonte) {
    marcadores.push({
      indice: principal.prazoMeses - 1,
      rotulo: `Empate · ${meses(principal.prazoMeses)}`,
      cor: COR.verde,
      corTexto: '#ffffff',
      linha: Math.abs(principal.prazoMeses - prazo) < horizonte * 0.18 ? 1 : 0,
    });
  }
  const svg = graficoLinhas({
    largura: larguraGrafico(modo),
    altura: curto ? 220 : (e.opcoes.alternativas ?? []).length ? 250 : 300,
    series: [
      { nome: nomeOutro, cor: corOutro, valores: outro.curva, tracejado: true, espessura: 2 },
      { nome: nomePrincipal, cor: corPrincipal, valores: principal.curva },
      { nome: 'Consórcio', cor: COR.ouro, valores: principal.curva.map(() => taxa), area: true, espessura: 3.5 },
    ],
    rotuloX: (i) => meses(i + 1),
    marcadores,
    pontos: [
      ...(e.opcoes.alternativas ?? [])
        .filter((a) => !a.atual)
        .sort((a, b) => a.prazo - b.prazo)
        // Rótulos alternam acima e abaixo do ponto, para prazos próximos não se cobrirem.
        .map((a, i) => ({
          indice: Math.min(a.prazo, horizonte) - 1,
          valor: a.taxaAdminPct,
          cor: COR.verde,
          rotulo: `G${a.grupo}`,
          rotuloAbaixo: i % 2 === 1,
        })),
      { indice: Math.min(prazo, horizonte) - 1, valor: taxa, cor: COR.marinho },
    ],
    formatoY: (v) => `${Math.round(v)}%`,
  });

  const prazoEmpateTexto = principal.prazoMeses ? meses(principal.prazoMeses) : 'acima de 35 anos';
  const titulo = ganha ? 'Neste prazo, o consórcio já é mais barato' : `Em ${meses(prazo)}, o financiamento sai mais barato`;
  const sub = ganha
    ? `Os juros do financiamento crescem com o prazo; a taxa de administração não. Com ${pct(taxa)} de taxa e juros de ${pct(juros, 2)} ao ano, o consórcio passa à frente a partir de ${prazoEmpateTexto}; este plano tem ${meses(prazo)}.`
    : `Os juros do financiamento crescem com o prazo; a taxa de administração não. Com ${pct(taxa)} de taxa, o consórcio passa à frente a partir de ${prazoEmpateTexto}, ou neste prazo com taxa de até ${pct(principal.taxaMaxPct, 2)}.`;

  const folga = e.entrada.credito * ((principal.taxaMaxPct - taxa) / 100);
  return `
    ${tituloSecao(`${num} · Onde o consórcio ganha`, titulo, sub, modo)}
    <div style="${LINHA_CARTOES}">
      ${cartaoNumero({
        rotulo: 'Prazo de empate',
        valor: prazoEmpateTexto,
        detalhe: `A partir daqui, os juros do ${principal.sistema} passam a taxa de ${pct(taxa)}`,
        escuro: !ganha,
        destaque: COR.verde,
      })}
      ${cartaoNumero({
        rotulo: 'Taxa de empate',
        valor: `até ${pct(principal.taxaMaxPct, 2)}`,
        detalhe: `De taxa de administração em ${meses(prazo)}; este plano tem ${pct(taxa)}`,
        destaque: ganha ? COR.verde : COR.ouro,
      })}
      ${cartaoNumero({
        rotulo: 'Juros de empate',
        valor: `${pct(principal.jurosEmpateAa, 2)} a.a.`,
        detalhe: `Financiamento acima disso custa mais que este plano; o de mercado é ${pct(juros, 2)}`,
        destaque: principal.jurosEmpateAa < juros ? COR.verde : COR.ouro,
      })}
    </div>
    ${caixaGrafico('Custo total em % do crédito, conforme o prazo', svg, [
      { nome: 'Consórcio (taxa)', cor: COR.ouro },
      { nome: principal.sistema, cor: corPrincipal },
      { nome: outro.sistema, cor: corOutro, tracejado: true },
    ])}
    <div style="display:flex;gap:12px;margin-top:14px;">
      <div style="flex:1;border-radius:14px;padding:14px 18px;background:${ganha ? '#EAF5EE' : '#FFF6E5'};">
        <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:${ganha ? COR.verde : '#9A6B00'};">${ganha ? 'Folga deste plano' : 'O que muda o resultado'}</div>
        <div style="font-size:12px;color:${COR.texto};margin-top:6px;line-height:1.5;">${
          ganha
            ? `Neste prazo, o financiamento custaria ${pct(principal.taxaMaxPct, 2)} do crédito; o consórcio custa ${pct(taxa)}. São <b>${formatBRL(folga)}</b> a favor do consórcio.`
            : `Um plano com prazo a partir de <b>${prazoEmpateTexto}</b>, ou com taxa de administração de até <b>${pct(principal.taxaMaxPct, 2)}</b>, inverte a conta a favor do consórcio. O especialista pode simular outros grupos e categorias com crédito parecido.`
        }</div>
      </div>
    </div>
    ${curto ? listaAlternativas(e) : tabelaAlternativas(e)}
    ${curto ? '' : `<div style="font-size:10.5px;color:${COR.suave};margin-top:14px;line-height:1.5;">
      Como ler: a faixa dourada é a taxa de administração deste plano, que não muda com o prazo. As curvas são os juros do financiamento do mesmo valor, em cada prazo. Onde a curva passa a faixa, o consórcio fica mais barato.
    </div>`}`;
};
